import React, { useMemo, useCallback } from "react";
import { Paper, Group, Text, Stack, Box, CloseButton } from "@mantine/core";
import { useTranslation } from "react-i18next";

import { Travel } from "@/utils/api/TravelService";

import { useUserProfileContext } from "@/contexts/UserProfileContext";

import { AppIcon } from "@/components/generic/AppIcon";

// ============================================
// Types
// ============================================

export interface TravelSelectionSummaryProps {
  /** Array of selected travel records */
  selectedTravels: Travel[];

  /** Callback to clear selection */
  onClearSelection?: () => void;

  /** Whether the summary is visible */
  visible?: boolean;
}

export interface SelectionTravelSummaryData {
  /** Number of selected items */
  count: number;
  /** Total distance sum */
  totalDistance: number;
  /** Number of unique days covered */
  totalDays: number;
  /** Whether all selected items have the same distance unit */
  isSameUnit: boolean;
  /** Distance unit of the first selected item */
  distanceUnit: string | null;
}

// ============================================
// Helper Functions
// ============================================

/**
 * Calculate selection summary data from selected travels
 */
export const calculateSelectionTravelSummary = (
  selectedTravels: Travel[]
): SelectionTravelSummaryData => {
  if (selectedTravels.length === 0) {
    return {
      count: 0,
      totalDistance: 0,
      totalDays: 0,
      isSameUnit: true,
      distanceUnit: null,
    };
  }

  // Calculate total distance
  const totalDistance = selectedTravels.reduce(
    (sum, t) => sum + (t.distance || 0),
    0
  );

  // Get distance unit from first selected item
  const distanceUnit = selectedTravels[0]?.distanceIn || null;

  // Check if all selected have the same distance unit
  const units = new Set(
    selectedTravels.map((t) => t.distanceIn?.toLowerCase()).filter(Boolean)
  );
  const isSameUnit = units.size <= 1;

  // Calculate unique days covered
  const uniqueDays = new Set<string>();
  selectedTravels.forEach((travel) => {
    if (travel.firstDttm) {
      const date = new Date(travel.firstDttm).toISOString().split("T")[0];
      uniqueDays.add(date);
    }
  });

  return {
    count: selectedTravels.length,
    totalDistance: Math.round(totalDistance * 10) / 10,
    totalDays: uniqueDays.size,
    isSameUnit,
    distanceUnit,
  };
};

/**
 * Format distance with unit
 */
const formatDistance = (distance: number, unit: string | null): string => {
  return `${distance.toLocaleString(undefined, {
    minimumFractionDigits: 0,
    maximumFractionDigits: 1,
  })} ${unit || "km"}`;
};

// ============================================
// Component
// ============================================

const TravelSelectionSummaryComponent: React.FC<TravelSelectionSummaryProps> = ({
  selectedTravels,
  onClearSelection,
  visible = true,
}) => {
  const { t } = useTranslation(["travels", "common"]);
  const { distanceLabelShort } = useUserProfileContext();

  // Calculate summary data
  const summary = useMemo(
    () => calculateSelectionTravelSummary(selectedTravels),
    [selectedTravels]
  );

  // Handle clear click
  const handleClear = useCallback(() => {
    onClearSelection?.();
  }, [onClearSelection]);

  // Format distance display
  const formattedDistance = useMemo(() => {
    if (!summary.isSameUnit) {
      return t("travels:selection.mixedUnits");
    }
    return formatDistance(
      summary.totalDistance,
      summary.distanceUnit || distanceLabelShort
    );
  }, [summary, distanceLabelShort, t]);

  // Don't render if no selection or not visible
  if (!visible || summary.count === 0) {
    return null;
  }

  return (
    <>
      {/* Mobile layout - sticky with gap from bottom */}
      <Box
        hiddenFrom="sm"
        style={{
          position: "fixed",
          bottom: 16,
          left: 12,
          right: 12,
          zIndex: 100,
        }}
      >
        <Paper
          shadow="xl"
          p="md"
          radius="md"
          style={{
            backgroundColor: "var(--mantine-color-blue-0)",
            border: "2px solid var(--mantine-color-blue-4)",
          }}
        >
          <Stack gap="sm">
            {/* Header row with count and close button */}
            <Group justify="space-between" wrap="nowrap">
              <Group gap="sm" wrap="nowrap">
                <Box
                  style={{
                    backgroundColor: "var(--mantine-color-blue-6)",
                    borderRadius: "50%",
                    width: 32,
                    height: 32,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AppIcon iconName="Check" size={18} color="white" />
                </Box>
                <Text size="md" fw={600} c="blue.8">
                  {t("travels:selection.selected", { count: summary.count })}
                </Text>
              </Group>
              <CloseButton
                size="lg"
                onClick={handleClear}
                aria-label={t("travels:selection.clear")}
                variant="subtle"
                c="blue.7"
              />
            </Group>

            {/* Stats row */}
            <Group gap="lg" wrap="wrap">
              {/* Total distance */}
              <Group gap="xs" wrap="nowrap" align="center">
                <AppIcon iconName="Route" size={20} color="var(--mantine-color-blue-6)" />
                <Text size="md" fw={600} c="blue.8">
                  {formattedDistance}
                </Text>
              </Group>

              {/* Total days */}
              {summary.totalDays > 0 && (
                <Group gap="xs" wrap="nowrap" align="center">
                  <AppIcon iconName="Calendar" size={20} color="var(--mantine-color-blue-6)" />
                  <Text size="md" fw={600} c="blue.8">
                    {t("travels:selection.days", { count: summary.totalDays })}
                  </Text>
                </Group>
              )}
            </Group>
          </Stack>
        </Paper>
      </Box>

      {/* Desktop layout - sticky with gap from bottom */}
      <Box
        visibleFrom="sm"
        style={{
          position: "fixed",
          bottom: 20,
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 100,
          maxWidth: 600,
          width: "calc(100% - 48px)",
        }}
      >
        <Paper
          shadow="xl"
          p="md"
          radius="md"
          style={{
            backgroundColor: "var(--mantine-color-blue-0)",
            border: "2px solid var(--mantine-color-blue-4)",
          }}
        >
          <Group justify="space-between" wrap="nowrap">
            <Group gap="xl" wrap="nowrap">
              {/* Selection count */}
              <Group gap="sm" wrap="nowrap">
                <Box
                  style={{
                    backgroundColor: "var(--mantine-color-blue-6)",
                    borderRadius: "50%",
                    width: 28,
                    height: 28,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <AppIcon iconName="Check" size={16} color="white" />
                </Box>
                <Text size="md" fw={600} c="blue.8">
                  {t("travels:selection.selected", { count: summary.count })}
                </Text>
              </Group>

              <Text c="blue.4" size="lg">•</Text>

              {/* Total distance */}
              <Group gap="xs" wrap="nowrap">
                <AppIcon iconName="Route" size={18} color="var(--mantine-color-blue-6)" />
                <Text size="md" fw={600} c="blue.8">
                  {formattedDistance}
                </Text>
              </Group>

              {/* Total days */}
              {summary.totalDays > 0 && (
                <>
                  <Text c="blue.4" size="lg">•</Text>
                  <Group gap="xs" wrap="nowrap">
                    <AppIcon iconName="Calendar" size={18} color="var(--mantine-color-blue-6)" />
                    <Text size="md" fw={600} c="blue.8">
                      {t("travels:selection.days", { count: summary.totalDays })}
                    </Text>
                  </Group>
                </>
              )}
            </Group>

            <CloseButton
              size="md"
              onClick={handleClear}
              aria-label={t("travels:selection.clear")}
              variant="subtle"
              c="blue.7"
            />
          </Group>
        </Paper>
      </Box>
    </>
  );
};

export const TravelSelectionSummary = React.memo(TravelSelectionSummaryComponent);